import { lazy, useEffect } from "react";
import { useSession } from "./state/session.js";
import { useRoute, navigate } from "./router.js";
import { Login } from "./screens/Login.js";
import { Register } from "./screens/Register.js";
import { AsyncBoundary } from "./ui/AsyncBoundary.js";
import { ToastHost } from "./ui/Toast.js";
import { useT } from "./i18n/index.js";
import { onWs } from "./api/ws.js";
import { useBalances } from "./state/balances.js";
import { useGameAudio } from "./audio/useGameAudio.js";
import { useProgressionPushes } from "./base/useProgressionPushes.js";
import { HqCelebration } from "./base/HqCelebration.js";
import { useBase } from "./base/state.js";
import { takeUnfinishedNote } from "./scene/blackbox.js";
import { installErrorReporting } from "./scene/errorReport.js";
import { api } from "./api/http.js";

/**
 * Read before anything renders, so it can only be a previous session's note (D96). Reading it in an
 * effect came after the colony's host had written this session's first note, and every report the
 * server ever received was that note — "renderer created", a third of a second old.
 */
const Shell = lazy(() => import("./screens/Shell.js").then(module => ({ default: module.Shell })));

const unfinished = takeUnfinishedNote();
let reported = false;

export function App() {
  const status = useSession((s) => s.status);
  const settings = useSession((s) => s.settings);
  const load = useSession((s) => s.load);
  const route = useRoute();
  const t = useT();
  useGameAudio(status, settings, route);
  useProgressionPushes();

  useEffect(() => {
    void load();
  }, [load]);

  // A session that ended badly left a note behind (D81). Once the player is signed in, it goes to the server:
  // a tab that dies on a phone leaves nothing else to look at.
  useEffect(() => {
    if (status !== "authed") return;
    const send = (step: string, note: Record<string, unknown>) => void api("POST", "/api/scene/report", { step, note }).catch(() => {
      /* a report that cannot be sent is not worth bothering the player about */
    });
    if (unfinished && !reported) {
      reported = true;
      send(unfinished.step, unfinished as unknown as Record<string, unknown>);
    }
    // An exception nobody sees is the other thing a player calls a crash; it goes to the same place (D96).
    return installErrorReporting(send);
  }, [status]);

  useEffect(() => {
    const offCredits = onWs("balances.credits", (e) => {
      const p = e.payload as { balance: number; reason: string };
      useBalances.getState().applyCreditChange(p.balance, p.reason);
    });
    const offRes = onWs("balances.resources", (e) => {
      const p = e.payload as { deltas: Record<string, number>; collectionRef?: string; reason?: string };
      useBalances.getState().applyResourceDeltas(p.deltas, p.collectionRef);
      if (p.reason === "production.auto_collect") void useBase.getState().refresh();
    });
    const offItem = onWs("inventory.item", (e) => {
      const p = e.payload as { itemId: string; qty: number };
      useSession.setState((s) => ({ inventory: [...s.inventory.filter((item) => item.itemId !== p.itemId), ...(p.qty > 0 ? [{ itemId: p.itemId, qty: p.qty }] : [])] }));
    });
    return () => {
      offCredits();
      offRes();
      offItem();
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("reduced", !!settings?.reducedMotion);
  }, [settings?.reducedMotion]);

  useEffect(() => {
    if (status === "anon" && route.startsWith("/app")) navigate("/login", true);
    if (status === "authed" && (route === "/" || route === "/login" || route === "/register")) navigate("/app", true);
  }, [status, route]);

  let screen: React.ReactNode;
  if (status === "loading") screen = <Centered>{t("common.loading")}</Centered>;
  else if (status === "anon") screen = route === "/register" ? <Register /> : <Login />;
  else screen = <AsyncBoundary resetKey={route} overlay><Shell route={route} /></AsyncBoundary>;

  return (
    <>
      {screen}
      <HqCelebration active={status === "authed" && route === "/app"} />
      <ToastHost />
    </>
  );
}

export function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100%", display: "grid", placeItems: "center", padding: 24 }} className="dim">
      {children}
    </div>
  );
}
